import React, { useState, useEffect } from 'react';
import { 
  Users, CheckCircle, XCircle, Clock, AlertCircle, 
  ChevronDown, ChevronUp, Star, Building, Phone, Mail,
  MapPin, BarChart3, TrendingUp, Filter, RefreshCw,
  Award, Loader2, ExternalLink, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ContractorApplications = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [updating, setUpdating] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadData();
  }, [statusFilter, scoreFilter]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadApplications(), loadStats()]);
    setLoading(false);
  };

  const loadApplications = async () => {
    try {
      let url = `${BACKEND_URL}/api/contractor-applications?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (scoreFilter) url += `&min_score=${scoreFilter}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setApplications(response.data.applications || []);
    } catch (err) {
      console.error('Failed to load applications:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      }
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/contractor-applications/stats`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    setUpdating(appId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/contractor-applications/${appId}/status`,
        { status: newStatus, notes: noteText || null },
        { headers: getAuthHeaders() }
      );
      toast.success(`Application ${newStatus}`);
      setNoteText('');
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const getScoreBadge = (score) => {
    if (!score) return null;
    const total = score.total_score;
    
    let bgColor, textColor, label;
    if (total >= 85) {
      bgColor = 'bg-green-100';
      textColor = 'text-green-700';
      label = 'Ideal Partner';
    } else if (total >= 70) {
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-700';
      label = 'Acceptable';
    } else if (total >= 60) {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-700';
      label = 'Conditional';
    } else {
      bgColor = 'bg-red-100';
      textColor = 'text-red-700';
      label = 'Below Threshold';
    }
    
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${bgColor}`}>
        <span className={`font-bold ${textColor}`}>{total}/100</span>
        <span className={`text-sm ${textColor}`}>{label}</span>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', label: 'Approved', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected', icon: XCircle },
      conditional: { color: 'bg-blue-100 text-blue-700', label: 'Conditional', icon: AlertCircle }
    };
    const { color, label, icon: Icon } = statusMap[status] || statusMap.pending_review;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const formatValue = (key, value) => {
    const mappings = {
      yearsInBusiness: {
        'under-1': 'Under 1 year',
        '1-3': '1-3 years',
        '3-5': '3-5 years',
        '5-10': '5-10 years',
        '10+': '10+ years'
      },
      annualRevenue: {
        'under-1m': 'Under $1M',
        '1m-3m': '$1M - $3M',
        '3m-5m': '$3M - $5M',
        '5m-10m': '$5M - $10M',
        '10m+': '$10M+'
      },
      responseTime: {
        'under-5-min': 'Under 5 minutes',
        '5-30-min': '5-30 minutes',
        'same-day': 'Same day',
        'next-day': 'Next day'
      },
      closeRate: {
        'under-20': 'Under 20%',
        '20-25': '20-25%',
        '25-35': '25-35%',
        '35+': '35%+'
      },
      startTimeframe: {
        'immediately': 'Immediately',
        'within-30-days': 'Within 30 days',
        'within-60-days': 'Within 60 days',
        'exploring': 'Just exploring'
      }
    };
    
    if (mappings[key] && mappings[key][value]) {
      return mappings[key][value];
    }
    return value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contractor-applications-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-7 h-7 text-orange-600" />
            Contractor Applications
          </h1>
          <p className="text-gray-600 mt-1">
            Review and score contractor qualification applications
          </p>
        </div>
        
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Applications</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">{stats.by_status?.pending_review || 0}</div>
            <div className="text-sm text-yellow-600">Pending Review</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="text-2xl font-bold text-green-700">{stats.by_score?.ideal_partner || 0}</div>
            <div className="text-sm text-green-600">Ideal Partners (85+)</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{stats.by_score?.acceptable || 0}</div>
            <div className="text-sm text-blue-600">Acceptable (70-85)</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-2xl font-bold text-red-700">{stats.by_score?.below_threshold || 0}</div>
            <div className="text-sm text-red-600">Below Threshold (&lt;60)</div>
          </div>
        </div>
      )}

      {/* Scoring Legend */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Scoring Criteria (100 Points Total)
        </h3>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-sm">
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Revenue</div>
            <div className="text-gray-500">$1M+ = 10 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Rating</div>
            <div className="text-gray-500">4.2+ = 10 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Reviews</div>
            <div className="text-gray-500">25+ = 10 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Response</div>
            <div className="text-gray-500">&lt;30min = 15 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Close Rate</div>
            <div className="text-gray-500">25%+ = 15 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">CRM</div>
            <div className="text-gray-500">Yes = 10 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Sales Team</div>
            <div className="text-gray-500">1 rep = 5, 2+ = 10</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Capacity</div>
            <div className="text-gray-500">10+ jobs = 10 pts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <div className="font-medium">Experience</div>
            <div className="text-gray-500">3+ yrs = 10 pts</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="conditional">Conditional</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Score</label>
          <select
            value={scoreFilter}
            onChange={(e) => setScoreFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Any Score</option>
            <option value="85">85+ (Ideal Partner)</option>
            <option value="70">70+ (Acceptable)</option>
            <option value="60">60+ (Conditional)</option>
          </select>
        </div>
      </div>

      {/* Applications List */}
      <div className="space-y-4">
        {applications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="font-semibold text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-500">Applications will appear here when contractors apply.</p>
          </div>
        ) : (
          applications.map((app) => (
            <div 
              key={app.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              data-testid={`application-${app.id}`}
            >
              {/* Application Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Building className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{app.company_name}</div>
                    <div className="text-sm text-gray-500">{app.contact_name} • {app.service_area}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {getScoreBadge(app.score)}
                  {getStatusBadge(app.status)}
                  <div className="text-sm text-gray-500">
                    {new Date(app.created_at).toLocaleDateString()}
                  </div>
                  {expandedApp === app.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedApp === app.id && (
                <div className="border-t border-gray-100">
                  <div className="p-6 grid md:grid-cols-2 gap-8">
                    {/* Left Column - Company Details */}
                    <div className="space-y-6">
                      {/* Contact Info */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          Company Information
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-500">Company:</span> {app.company_name}</p>
                          <p><span className="text-gray-500">Contact:</span> {app.contact_name}</p>
                          <p className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {app.phone}
                          </p>
                          <p className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            {app.email}
                          </p>
                          {app.website && (
                            <p className="flex items-center gap-1">
                              <ExternalLink className="w-3 h-3 text-gray-400" />
                              <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {app.website}
                              </a>
                            </p>
                          )}
                          <p className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            {app.service_area}
                          </p>
                        </div>
                      </div>

                      {/* Business Profile */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Business Profile</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Experience</div>
                            <div className="font-medium">{formatValue('yearsInBusiness', app.years_in_business)}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Revenue</div>
                            <div className="font-medium">{formatValue('annualRevenue', app.annual_revenue)}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Crews</div>
                            <div className="font-medium">{app.number_of_crews}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Sales Reps</div>
                            <div className="font-medium">{app.number_of_sales_reps}</div>
                          </div>
                        </div>
                      </div>

                      {/* Lead Handling */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Lead Handling</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Response Time</div>
                            <div className="font-medium">{formatValue('responseTime', app.response_time)}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Monthly Leads</div>
                            <div className="font-medium">{app.monthly_leads}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Close Rate</div>
                            <div className="font-medium">{formatValue('closeRate', app.close_rate)}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Uses CRM</div>
                            <div className="font-medium">{app.has_crm === 'yes' ? 'Yes' : 'No'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Reputation */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Star className="w-4 h-4" />
                          Reputation
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Google Rating</div>
                            <div className="font-medium flex items-center gap-1">
                              {app.average_rating}
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-gray-500">Reviews</div>
                            <div className="font-medium">{app.number_of_reviews}</div>
                          </div>
                        </div>
                        {app.google_profile_link && (
                          <a 
                            href={app.google_profile_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Google Profile
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Scoring & Actions */}
                    <div className="space-y-6">
                      {/* Score Breakdown */}
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Score Breakdown
                        </h4>
                        
                        <div className="space-y-2">
                          {app.score?.breakdown && Object.entries(app.score.breakdown).map(([key, data]) => (
                            <div key={key} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 capitalize">{key.replace('_', ' ')}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      data.points === data.max ? 'bg-green-500' :
                                      data.points > 0 ? 'bg-yellow-500' : 'bg-red-300'
                                    }`}
                                    style={{ width: `${(data.points / data.max) * 100}%` }}
                                  />
                                </div>
                                <span className={`font-medium w-12 text-right ${
                                  data.points === data.max ? 'text-green-600' :
                                  data.points > 0 ? 'text-yellow-600' : 'text-red-500'
                                }`}>
                                  {data.points}/{data.max}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                          <span className="font-semibold text-gray-900">Total Score</span>
                          <span className={`text-2xl font-bold ${
                            app.score?.total_score >= 85 ? 'text-green-600' :
                            app.score?.total_score >= 70 ? 'text-blue-600' :
                            app.score?.total_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {app.score?.total_score}/100
                          </span>
                        </div>
                        
                        <div className={`mt-3 p-3 rounded-lg text-center font-medium ${
                          app.score?.recommendation_color === 'green' ? 'bg-green-100 text-green-700' :
                          app.score?.recommendation_color === 'blue' ? 'bg-blue-100 text-blue-700' :
                          app.score?.recommendation_color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {app.score?.recommendation_text}
                        </div>
                      </div>

                      {/* Why Better Than Average */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Why They're Better Than Average
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                          {app.why_better_than_average || 'No response provided'}
                        </div>
                      </div>

                      {/* Commitment */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-sm text-blue-600">Ready to Start</div>
                        <div className="font-medium text-blue-900">
                          {formatValue('startTimeframe', app.start_timeframe)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Update Status</h4>
                        
                        <div className="mb-3">
                          <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add a note (optional)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                            rows={2}
                          />
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => updateStatus(app.id, 'approved')}
                            disabled={updating === app.id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            {updating === app.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            onClick={() => updateStatus(app.id, 'conditional')}
                            disabled={updating === app.id}
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Conditional
                          </Button>
                          <Button
                            onClick={() => updateStatus(app.id, 'rejected')}
                            disabled={updating === app.id}
                            variant="outline"
                            className="border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContractorApplications;
